import { StringData } from "../..";
import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";

/** Attribute selector for FPS tracker elements */
const ATTR_FPS = "data-fps";

/**
 * FPS Tracker Module.
 * Broadcasts frame rate to elements with `data-fps` attribute.
 * Also creates an optional debug display element.
 */
export class StringFPSTracker extends StringModule {
  private displayElement: HTMLDivElement | null = null;
  private intervalId: number = 0;
  private frameCount = 0;

  /** Cached elements with data-fps attribute */
  private fpsElements: Set<HTMLElement> = new Set();

  /** MutationObserver for DOM changes */
  private observer: MutationObserver | null = null;

  /** Last known FPS to avoid redundant updates */
  private lastFps = -1;

  constructor(context: StringContext) {
    super(context);
    this._type = 2;
  }

  /**
   * Initializes the visual FPS counter, scans for elements, and starts interval.
   */
  onInit(): void {
    if (this.data.system.fpsTracker) {
      this.createDisplayElement();
    }

    // Listen for visibility toggle
    this.events.on("tracker:fps:visible", this.onVisibilityChange.bind(this));

    this.scanElements();
    this.observeDOM();

    this.intervalId = window.setInterval(() => {
      this.updateFPS(this.frameCount);
      this.frameCount = 0;
    }, 1000);
  }

  /**
   * Increments the frame counter each frame.
   */
  onFrame(_data: StringData): void {
    this.frameCount++;
  }

  /**
   * Cleans up DOM, observer, and interval.
   */
  destroy(): void {
    clearInterval(this.intervalId);
    this.observer?.disconnect();
    this.removeDisplayElement();
    this.fpsElements.clear();
  }

  /**
   * Handles visibility toggle from external API.
   */
  private onVisibilityChange(visible: boolean): void {
    if (visible) {
      this.createDisplayElement();
    } else {
      this.removeDisplayElement();
    }
  }

  /**
   * Removes the display element and its styles.
   */
  private removeDisplayElement(): void {
    this.displayElement?.remove();
    this.displayElement = null;
  }

  /**
   * Updates all tracked elements with current FPS.
   */
  private updateFPS(fps: number): void {
    if (fps === this.lastFps) return;
    this.lastFps = fps;

    const fpsStr = String(fps);

    // Update cached elements
    for (const el of this.fpsElements) {
      if (el.isConnected) {
        el.setAttribute(ATTR_FPS, fpsStr);
      }
    }

    // Update debug display
    if (this.displayElement) {
      this.displayElement.setAttribute(ATTR_FPS, fpsStr);
    }

    this.events.emit("fps", fps);
  }

  /**
   * Scans document for elements with data-fps attribute.
   */
  private scanElements(): void {
    this.fpsElements.clear();

    document.querySelectorAll<HTMLElement>(`[${ATTR_FPS}]`).forEach((el) => {
      if (el !== this.displayElement) {
        this.fpsElements.add(el);
      }
    });
  }

  /**
   * Observes DOM for added/removed elements with data-fps attribute.
   */
  private observeDOM(): void {
    this.observer = new MutationObserver((mutations) => {
      let needsRescan = false;

      for (const mutation of mutations) {
        // Check added nodes
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.hasAttribute(ATTR_FPS)) needsRescan = true;
            if (el.querySelector(`[${ATTR_FPS}]`)) needsRescan = true;
          }
        }

        // Check removed nodes - clean from cache
        for (const node of mutation.removedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.fpsElements.delete(node as HTMLElement);
          }
        }
      }

      if (needsRescan) {
        this.scanElements();
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Creates and styles the floating FPS display.
   */
  private createDisplayElement(): void {
    if (this.displayElement) return;

    const el = document.createElement("div");

    Object.assign(el.style, {
      position: "fixed",
      bottom: "10px",
      right: "10px",
      backgroundColor: "#000",
      color: "#fff",
      padding: "4px 8px",
      fontSize: "12px",
      fontFamily: "monospace",
      border: "1px solid rgba(255,255,255,0.2)",
      zIndex: "1000",
      pointerEvents: "none",
    });

    el.setAttribute(ATTR_FPS, "0");
    document.body.appendChild(el);

    // Inject dynamic CSS
    const styleId = "string-fps-tracker-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        [${ATTR_FPS}]::before {
          content: 'FPS: ' attr(${ATTR_FPS});
        }
      `;
      document.head.appendChild(style);
    }

    this.displayElement = el;
  }
}
